import { green, grey, red } from "@ant-design/colors"
import { faPaperPlane, faPause, faThumbsUp, faXmarkToSlot } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Col, Input, Modal, Row, Skeleton, Space } from "antd"
import dayjs, { Dayjs } from 'dayjs'
import { useMemo, useState } from "react"
import { projectTypeOptionsFor } from "../../../common/constants"
import useServiceTicketsEnabled from "../../../hooks/useServiceTicketsEnabled"
import VSpace from "../../common/VSpace"
import { DatacenterReportTypes, DateRanges, getDatacenterReportTypeNames, getFiltersFromJson, ReportFilterViewModel } from "../datacenterModel"
import { useCreateFiltersMutation, useDeleteFiltersMutation, useGetFiltersQuery } from "../datacenterQueries"
import ReportFilters from "../ReportFilters"
import ReportSavedFiltersList from "../ReportSavedFiltersList"
import SaveFilterModal from "../SaveFilterModal"
import ItemReportFilterDetails from "./ItemReportFilterDetails"
import ItemsReportItemsList from "./ItemsReportItemsList"
import { aggregateItemsData, getFilteredItems } from "./itemsReportModel"
import { useItemsReportDataQuery } from "./itemsReportQueries"

export type StatusesFilters = 'isApproved' | 'isOnHold' | 'lost' | 'draft'

export type ItemsReportFilters = {
  startDate: Dayjs
  endDate: Dayjs
  searchTerm: string
  statuses: StatusesFilters[]
  jobTypes: number[]
  dateRange: DateRanges
}

const ItemsReport = () => {
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const today = dayjs()
  const defaultFilters: ItemsReportFilters = { startDate: today.subtract(6, 'months'), endDate: today, searchTerm: '', statuses: [], jobTypes: [], dateRange: 'custom' }

  const [filters, setFilters] = useState<ItemsReportFilters>(defaultFilters)
  const [open, setOpen] = useState(false)
  const [deleteFilterModalConfirm, deleteFiltersModalContext] = Modal.useModal()
  const [filterName, setFilterName] = useState('')

  const itemsReportQuery = useItemsReportDataQuery(
    filters.startDate.toISOString().split('T')[0], 
    filters.endDate.toISOString().split('T')[0]
  )
  const items = aggregateItemsData(itemsReportQuery.data || []) || []
  const searchTermLower = filters.searchTerm.toLowerCase()
  const statusSet = new Set(filters.statuses)
  const jobTypesSet = new Set(filters.jobTypes)

  const filteredItems = useMemo(() => getFilteredItems(items, searchTermLower, statusSet, jobTypesSet), [items, searchTermLower, statusSet])

  const getFiltersQuery = useGetFiltersQuery(DatacenterReportTypes.Items)
  const createFiltersMutation = useCreateFiltersMutation()
  const deleteFiltersMutation = useDeleteFiltersMutation()

  const filtersList: ReportFilterViewModel[] = getFiltersQuery.data?.map((x) => {
    return {
      ...x,
      filters: getFiltersFromJson(x.filters)
    }
  }).sort((a, b) => a.order - b.order) || []

  if (itemsReportQuery.isLoading) {
    return <Skeleton active />
  }

  return (
    <Row gutter={16}>
      {
        (filtersList.length > 0) && <Col span={4}>
          <ReportSavedFiltersList
            filtersList={filtersList}
            onFilterSelect={(filters) => setFilters(filters as ItemsReportFilters)}
            onDeleteFilters={(selectedFiltersIds) => {
              deleteFilterModalConfirm.confirm({
                title: 'Do you want to delete the selected filters?',
                content: 'This action cannot be undone',
                onOk: () => {
                  deleteFiltersMutation.mutate({ filtersIds: selectedFiltersIds, reportType: DatacenterReportTypes.Items })
                },
                okText: 'Delete',
                okButtonProps: {
                  loading: deleteFiltersMutation.isLoading,
                  danger: true
                }
              })
            }}
            renderFilterDetails={(filterItem) => <ItemReportFilterDetails filterItem={filterItem} />}
            reportType={DatacenterReportTypes.Items}
          />
        </Col>
      }
      <Col span={filtersList.length > 0 ? 20 : 24}>
        <VSpace>
          <Input.Search placeholder="Filter by item name" value={filters.searchTerm} onChange={e => setFilters({ ...filters, searchTerm: e.target.value })} />
          <ReportFilters
            filters={filters}
            onFiltersChange={setFilters}
            isDataLoading={itemsReportQuery.isLoading}
            defaultFilters={defaultFilters}
            onSaveFilters={() => setOpen(true)}
            selectOptions={{
              statuses: {
                mode: 'multiple',
                options: [
                  {
                    label: <Space><FontAwesomeIcon icon={faThumbsUp} color={green.primary} size='lg' />Approved</Space>,
                    value: 'isApproved' as StatusesFilters,
                  },
                  {
                    label: <Space><FontAwesomeIcon icon={faPause} color={grey[0]} size='lg' />On Hold</Space>,
                    value: 'isOnHold' as StatusesFilters
                  },
                  {
                    label: <Space><FontAwesomeIcon icon={faXmarkToSlot} color={red.primary} size='lg' />Lost</Space>,
                    value: 'lost' as StatusesFilters
                  },
                  {
                    label: <Space><FontAwesomeIcon icon={faPaperPlane} color={grey[0]} size='lg' />Draft</Space>,
                    value: 'draft' as StatusesFilters
                  }
                ],
                placeholder: 'Select Statuses',
                style: { width: 300 },
                allowClear: true,
                onChange: (values: StatusesFilters[]) => setFilters({ ...filters, statuses: values })
              },
              jobTypes: {
                mode: 'multiple',
                options: projectTypeOptions,
                placeholder: 'Select Job Types',
                style: { width: 300 },
                allowClear: true,
                onChange: (values: number[]) => setFilters({ ...filters, jobTypes: values })
              }
            }}
          />
          <ItemsReportItemsList items={filteredItems} dateRange={{ start: filters.startDate, end: filters.endDate }} />
        </VSpace>
      </Col>

      <SaveFilterModal
        title='Save the Filter'
        okText='Save Filter'
        cancelText='Cancel'
        onOk={() => {
          createFiltersMutation.mutate({
            request: {
              reportId: DatacenterReportTypes.Items,
              name: filterName,
              reportTypeName: getDatacenterReportTypeNames(DatacenterReportTypes.Items),
              filters: JSON.stringify(filters)
            },
            reportType: DatacenterReportTypes.Items
          })
          setOpen(false)
          setFilterName('')
        }}
        onCancel={() => {
          setFilterName('')
          setOpen(false)
        }}
        open={open}
        inputValue={filterName}
        onInputChange={(value) => setFilterName(value)}
      />
      {deleteFiltersModalContext}
    </Row>
  )
}

export default ItemsReport